import { create } from 'zustand';
import { Project } from '@/lib/api/projects';

// Build status from API
export type BuildStatus =
  | 'queued'
  | 'running'
  | 'success'
  | 'failed'
  | 'timeout'
  | 'cancelled';

// Combined publish status for UI
export type PublishStatus =
  | 'idle'
  | 'checking'      // Checking for uncommitted changes
  | 'queued'        // Build queued
  | 'building'      // Build in progress
  | 'deploying'     // Deploy in progress
  | 'success'       // Publish complete
  | 'failed';       // Publish failed

export interface PublishState {
  status: PublishStatus;
  buildId: string | null;
  buildStatus: BuildStatus | null;
  error: string | null;
  publicUrl: string | null;
  startedAt: number | null;
  // For fast publish (skips build)
  fastPublish: boolean;
  // Track which project this state belongs to (prevents cross-project state leakage)
  projectId: string | null;
}

const initialPublishState: PublishState = {
  status: 'idle',
  buildId: null,
  buildStatus: null,
  error: null,
  publicUrl: null,
  startedAt: null,
  fastPublish: false,
  projectId: null,
};

interface ProjectStore {
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  updateProject: (updates: Partial<Project>) => void;
  // Counter to trigger project list refresh (incremented on rename, delete, etc.)
  projectListVersion: number;
  incrementProjectListVersion: () => void;
  // Legacy publishing state - prevents rename during publish
  isPublishing: boolean;
  setIsPublishing: (isPublishing: boolean) => void;
  // Full publish state for UI synchronization
  publishState: PublishState;
  setPublishState: (state: Partial<PublishState>) => void;
  resetPublishState: () => void;
}

export const useProjectStore = create<ProjectStore>((set) => ({
  currentProject: null,
  setCurrentProject: (project) => set({ currentProject: project }),
  updateProject: (updates) => set((state) => ({
    currentProject: state.currentProject
      ? { ...state.currentProject, ...updates }
      : null
  })),
  // Counter to trigger project list refresh
  projectListVersion: 0,
  incrementProjectListVersion: () => set((state) => ({
    projectListVersion: state.projectListVersion + 1
  })),
  // Legacy publishing state - prevents rename during publish
  isPublishing: false,
  setIsPublishing: (isPublishing) => set({ isPublishing }),
  // Full publish state
  publishState: initialPublishState,
  setPublishState: (newState) => set((state) => ({
    publishState: { ...state.publishState, ...newState }
  })),
  resetPublishState: () => set({ publishState: initialPublishState }),
}));
